/** The files an operator is attaching to a message, uploaded into their holding area as
 * soon as they are picked, pasted or dropped, so Send only waits on the last one. An
 * image goes to the model as a picture; any other file is a document the agent reads
 * (docs/Y2026-Q4/design-conversation-document-attachments.md). */
import { useCallback, useEffect, useRef, useState } from "react";

import { useAgentsHttp } from "../useAgentsHttp";
import type { HeldFile } from "./useStatefulAgents";

type Kind = "image" | "document";

// Mirrors conversation_attachments.MAX_PER_MESSAGE / MAX_BYTES / IMAGE_TYPES - must agree.
const MAX_PER_MESSAGE: Record<Kind, number> = { image: 5, document: 5 };
const MAX_BYTES: Record<Kind, number> = {
  image: 20 * 1024 * 1024,
  document: 50 * 1024 * 1024,
};
export const IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
];
export const MAX_ATTACHMENTS = MAX_PER_MESSAGE.image + MAX_PER_MESSAGE.document;

export interface Attachment {
  id: string;
  name: string;
  kind: Kind;
  /** A local preview of an image; null for a document or a format the browser cannot
   * draw (HEIC). */
  previewUrl: string | null;
  status: "uploading" | "ready" | "failed";
  error: string | null;
  s3Key: string | null;
}

/** What a message carries, as its request body sends it. */
export interface HeldAttachments {
  images: HeldFile[];
  documents: HeldFile[];
}

export interface Attachments {
  items: Attachment[];
  add: (files: File[]) => void;
  /** For the message box: a pasted file is attached rather than pasted as text. */
  onPaste: (event: React.ClipboardEvent) => void;
  remove: (id: string) => void;
  clear: () => void;
  /** Drop what a message just carried, keeping anything attached since it was sent. */
  release: (sent: HeldAttachments) => void;
  /** The uploaded files, in the order they were added. */
  ready: HeldAttachments;
  uploading: boolean;
  /** An upload failed: the operator removes it before sending, so it is never dropped. */
  failed: boolean;
  /** A file refused before upload (too big, too many). */
  rejection: string | null;
}

const canPreview = (type: string) => type !== "image/heic" && type !== "image/heif";

// Chrome and Firefox off macOS report HEIC with an empty type.
const typeOf = (file: File): string => {
  if (file.type) return file.type;
  const ext = file.name.toLowerCase().split(".").pop();
  return ext === "heic" ? "image/heic" : ext === "heif" ? "image/heif" : "";
};

const kindOf = (file: File): Kind => (IMAGE_TYPES.includes(typeOf(file)) ? "image" : "document");

const held = (items: Attachment[], kind: Kind): HeldFile[] =>
  items.flatMap((item) =>
    item.kind === kind && item.status === "ready" && item.s3Key
      ? [{ s3_key: item.s3Key, name: item.name }]
      : [],
  );

export const useAttachments = (): Attachments => {
  const http = useAgentsHttp();
  const [items, setItems] = useState<Attachment[]>([]);
  const [rejection, setRejection] = useState<string | null>(null);
  const counts = useRef<Record<Kind, number>>({ image: 0, document: 0 });
  const previews = useRef(new Set<string>());

  const update = (id: string, patch: Partial<Attachment>) =>
    setItems((all) => all.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const upload = useCallback(
    async (id: string, file: File, type: string) => {
      try {
        const target = await http<{ url: string; s3_key: string; content_type: string }>(
          "/stateful-runs/attachment-upload-url",
          { file_name: file.name || "pasted-image.png", content_type: type },
        );
        const put = await fetch(target.url, {
          method: "PUT",
          body: file,
          headers: { "content-type": target.content_type },
        });
        if (!put.ok) throw new Error(`upload failed (${put.status})`);
        update(id, { status: "ready", s3Key: target.s3_key });
      } catch (e) {
        update(id, {
          status: "failed",
          error: e instanceof Error ? e.message : "upload failed",
        });
      }
    },
    [http],
  );

  const add = useCallback(
    (files: File[]) => {
      setRejection(null);
      const refused: string[] = [];
      const accepted: File[] = [];
      const taking: Record<Kind, number> = { ...counts.current };
      for (const file of files) {
        const kind = kindOf(file);
        if (file.size > MAX_BYTES[kind])
          refused.push(`${file.name} is larger than ${MAX_BYTES[kind] / (1024 * 1024)} MB`);
        else if (taking[kind] >= MAX_PER_MESSAGE[kind])
          refused.push(`at most ${MAX_PER_MESSAGE[kind]} ${kind}s per message`);
        else {
          taking[kind] += 1;
          accepted.push(file);
        }
      }
      if (refused.length) setRejection([...new Set(refused)].join("; "));
      counts.current = taking;
      const added = accepted.map((file) => {
        const kind = kindOf(file);
        const previewUrl =
          kind === "image" && canPreview(typeOf(file)) ? URL.createObjectURL(file) : null;
        if (previewUrl) previews.current.add(previewUrl);
        return {
          file,
          item: {
            id: crypto.randomUUID(),
            // A pasted screenshot arrives as "image.png"; keep it distinguishable.
            name: file.name || "pasted image",
            kind,
            previewUrl,
            status: "uploading" as const,
            error: null,
            s3Key: null,
          },
        };
      });
      setItems((all) => [...all, ...added.map((a) => a.item)]);
      for (const { file, item } of added) void upload(item.id, file, typeOf(file));
    },
    [upload],
  );

  const forget = (item: Attachment) => {
    if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
      previews.current.delete(item.previewUrl);
    }
  };

  const recount = (kept: Attachment[]) => {
    counts.current = {
      image: kept.filter((item) => item.kind === "image").length,
      document: kept.filter((item) => item.kind === "document").length,
    };
  };

  const remove = useCallback((id: string) => {
    setItems((all) => {
      const gone = all.find((item) => item.id === id);
      if (gone) forget(gone);
      const kept = all.filter((item) => item.id !== id);
      recount(kept);
      return kept;
    });
    setRejection(null);
  }, []);

  const clear = useCallback(() => {
    setItems((all) => {
      all.forEach(forget);
      return [];
    });
    recount([]);
    setRejection(null);
  }, []);

  const release = useCallback((sent: HeldAttachments) => {
    const keys = new Set([...sent.images, ...sent.documents].map((file) => file.s3_key));
    setItems((all) => {
      const kept = all.filter((item) => !(item.s3Key && keys.has(item.s3Key)));
      all.filter((item) => !kept.includes(item)).forEach(forget);
      recount(kept);
      return kept;
    });
  }, []);

  useEffect(() => {
    const live = previews.current;
    return () => live.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      const files = Array.from(event.clipboardData.files);
      if (files.length) {
        event.preventDefault();
        add(files);
      }
    },
    [add],
  );

  return {
    items,
    add,
    onPaste,
    remove,
    clear,
    release,
    ready: { images: held(items, "image"), documents: held(items, "document") },
    uploading: items.some((item) => item.status === "uploading"),
    failed: items.some((item) => item.status === "failed"),
    rejection,
  };
};
